'use client';

import { c as _c } from "react/compiler-runtime";
import { createContext, use, useCallback, useEffect, useRef, useState } from 'react';
import { Tabs as TabsPrimitive } from 'radix-ui';
import { tv } from 'tailwind-variants';
import { useIsClient } from '../hooks/useIsClient';
import { label } from '../typography/Label';
import type { VariantProps } from 'tailwind-variants';

// Define the context
export type TabsOrientation = 'horizontal' | 'vertical';
interface TabsListContextValue {
  variant?: VariantProps<typeof tabsList>['variant'];
}
const TabsListContext = createContext<TabsListContextValue>({});
const TabsOrientationContext = createContext<TabsOrientation>('horizontal');

// Hook to track active tab position for animated border
function useActiveTabPosition() {
  const $ = _c(5);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      left: 0,
      width: 0
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [activeTabPosition, setActiveTabPosition] = useState(t0);
  const isMounted = useIsClient();
  const tabsListRef = useRef(null);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (!tabsListRef.current) {
        return;
      }
      const activeTab = tabsListRef.current.querySelector("[data-state=\"active\"]") as HTMLElement;
      if (activeTab) {
        const listRect = tabsListRef.current.getBoundingClientRect();
        const tabRect = activeTab.getBoundingClientRect();
        setActiveTabPosition({
          left: tabRect.left - listRect.left,
          width: tabRect.width
        });
      } else {
        setActiveTabPosition({
          left: 0,
          width: 0
        });
      }
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const updateActiveTabPosition = t1;
  let t2;
  if ($[2] !== activeTabPosition || $[3] !== isMounted) {
    t2 = {
      activeTabPosition,
      isMounted,
      tabsListRef,
      updateActiveTabPosition
    };
    $[2] = activeTabPosition;
    $[3] = isMounted;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
const tabs = tv({
  base: `
    flex
    flex-1
    flex-col
    min-h-0
    min-w-0
    data-[orientation=vertical]:flex-row
    data-[orientation=vertical]:gap-4
  `,
  variants: {
    width: {
      full: `
        w-full
      `,
      fit: `
        w-fit
      `
    }
  }
});
interface TabsProps extends React.ComponentProps<typeof TabsPrimitive.Root>, VariantProps<typeof tabs> {}
function Tabs(t0) {
  const $ = _c(16);
  let className;
  let props;
  let t1;
  let width;
  if ($[0] !== t0) {
    ({
      className,
      width,
      orientation: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
    $[4] = width;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
    width = $[4];
  }
  const orientation = t1 === undefined ? "horizontal" : t1;
  let t2;
  if ($[5] !== className || $[6] !== width) {
    t2 = tabs({
      className,
      width
    });
    $[5] = className;
    $[6] = width;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== orientation || $[9] !== props || $[10] !== t2 || $[11] !== width) {
    t3 = <TabsPrimitive.Root className={t2} data-slot="tabs" data-width={width} orientation={orientation} {...props} />;
    $[8] = orientation;
    $[9] = props;
    $[10] = t2;
    $[11] = width;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== orientation || $[14] !== t3) {
    t4 = <TabsOrientationContext.Provider value={orientation}>{t3}</TabsOrientationContext.Provider>;
    $[13] = orientation;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
}
const tabsList = tv({
  base: `
    inline-flex
    h-9
    items-center
    justify-start
    in-data-[width=full]:w-full
    in-data-[width=fit]:w-fit
    relative
    data-[orientation=vertical]:h-auto
    data-[orientation=vertical]:flex-col
    data-[orientation=vertical]:items-stretch
    data-[orientation=vertical]:gap-1
    `,
  variants: {
    variant: {
      default: `
        border-border-secondary border-b
        data-[orientation=vertical]:border-b-0
        `,
      outline: `
        bg-muted
        rounded-lg
        p-[3px]
      `
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
const animatedBorder = tv({
  base: `
    absolute
    bottom-0
    h-0.5
    bg-border-primary
    transition-all
    duration-300
    ease-out
    pointer-events-none
  `,
  variants: {
    variant: {
      default: `
        block
      `,
      outline: `
        hidden
      `
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
interface TabsListProps extends React.ComponentProps<typeof TabsPrimitive.List>, VariantProps<typeof tabsList> {}

// Use the variant prop and provide context
function TabsList(t0) {
  const $ = _c(27);
  let className;
  let props;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      variant,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = variant;
  } else {
    className = $[1];
    props = $[2];
    variant = $[3];
  }
  const {
    activeTabPosition,
    isMounted,
    tabsListRef,
    updateActiveTabPosition
  } = useActiveTabPosition();
  const orientation = use(TabsOrientationContext);
  const isVertical = orientation === "vertical";
  let t1;
  let t2;
  if ($[4] !== isVertical || $[5] !== tabsListRef || $[6] !== updateActiveTabPosition) {
    t1 = () => {
      if (isVertical || !tabsListRef.current) {
        return;
      }
      const tabsRoot = tabsListRef.current.closest("[data-slot=\"tabs\"]") as HTMLElement;
      if (tabsRoot) {
        const mutationObserver = new MutationObserver(() => {
          setTimeout(updateActiveTabPosition, 0);
        });
        mutationObserver.observe(tabsRoot, {
          attributes: true,
          subtree: true,
          attributeFilter: ["data-state"]
        });
        const resizeObserver = new ResizeObserver(() => {
          updateActiveTabPosition();
        });
        resizeObserver.observe(tabsListRef.current);
        setTimeout(updateActiveTabPosition, 0);
        return () => {
          mutationObserver.disconnect();
          resizeObserver.disconnect();
        };
      }
    };
    t2 = [isVertical, tabsListRef, updateActiveTabPosition];
    $[4] = isVertical;
    $[5] = tabsListRef;
    $[6] = updateActiveTabPosition;
    $[7] = t1;
    $[8] = t2;
  } else {
    t1 = $[7];
    t2 = $[8];
  }
  useEffect(t1, t2);
  let t3;
  if ($[9] !== variant) {
    t3 = {
      variant
    };
    $[9] = variant;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== className || $[12] !== variant) {
    t4 = tabsList({
      variant,
      className
    });
    $[11] = className;
    $[12] = variant;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== activeTabPosition || $[15] !== isMounted || $[16] !== isVertical || $[17] !== variant) {
    t5 = !isVertical && <div className={animatedBorder({
      variant
    })} data-slot="tabs-active-border" style={{
      left: `${activeTabPosition.left}px`,
      width: `${activeTabPosition.width}px`,
      opacity: isMounted ? 1 : 0,
      visibility: isMounted ? "visible" : "hidden"
    }} suppressHydrationWarning={true} />;
    $[14] = activeTabPosition;
    $[15] = isMounted;
    $[16] = isVertical;
    $[17] = variant;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== props || $[20] !== t4 || $[21] !== t5 || $[22] !== tabsListRef) {
    t6 = <TabsPrimitive.List className={t4} data-slot="tabs-list" ref={tabsListRef} tabIndex={0} {...props}>{props.children}{t5}</TabsPrimitive.List>;
    $[19] = props;
    $[20] = t4;
    $[21] = t5;
    $[22] = tabsListRef;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== t3 || $[25] !== t6) {
    t7 = <TabsListContext.Provider value={t3}>{t6}</TabsListContext.Provider>;
    $[24] = t3;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  return t7;
}
const tabsTrigger = tv({
  extend: label,
  base: `
    data-[state=active]:bg-background
    data-[state=active]:text-primary
    data-[state=active]:font-medium
    dark:data-[state=active]:text-foreground
    dark:data-[state=active]:bg-transparent
    dark:data-[state=active]:border-input
    disabled:text-content-tertiary
    disabled:opacity-60
    text-content-tertiary
    inline-flex
    items-center
    justify-center
    gap-1.5
    border-transparent
    px-2
    py-1
    w-fit
    whitespace-nowrap
    transition-[color,box-shadow]
    focus-visible:rounded
    focus-visible:outline-none
    focus-visible:inset-ring-1
    focus-visible:inset-ring-primary
    disabled:pointer-events-none
    [&_svg]:pointer-events-none
    [&_svg]:shrink-0
    [&_svg:not([class*=_size-_])]:size-4
    cursor-pointer
    hover:text-content-primary
  `,
  variants: {
    variant: {
      default: `
        h-full
        border-b-2
        border-transparent
        data-[orientation=vertical]:h-auto
        data-[orientation=vertical]:w-full
        data-[orientation=vertical]:justify-start
        data-[orientation=vertical]:text-left
        data-[orientation=vertical]:whitespace-normal
        data-[orientation=vertical]:border-b-0
        data-[orientation=vertical]:rounded-md
        data-[orientation=vertical]:py-2
        data-[orientation=vertical]:hover:bg-background-secondary
        data-[orientation=vertical]:data-[state=active]:bg-background-secondary
        dark:data-[orientation=vertical]:data-[state=active]:bg-background-secondary
        data-[orientation=vertical]:data-[state=active]:text-content-primary
        data-[orientation=vertical]:data-[state=active]:font-medium
        data-[orientation=vertical]:data-[state=active]:shadow-[0px_1px_4px_0px_rgba(0,0,0,0.04)]
      `,
      outline: `
        h-[calc(100%-1px)]
        rounded-md
        border
        border-transparent
        data-[state=active]:shadow-sm
      `
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});

// Consume context and apply variant
function TabsTrigger(t0) {
  const $ = _c(4);
  const {
    ...props
  } = t0;
  const {
    variant
  } = use(TabsListContext);
  const t1 = TabsPrimitive;
  const t2 = tabsTrigger({
    ...props,
    variant
  });
  let t3;
  if ($[0] !== props || $[1] !== t1.Trigger || $[2] !== t2) {
    t3 = <t1.Trigger className={t2} data-slot="tabs-trigger" {...props} />;
    $[0] = props;
    $[1] = t1.Trigger;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
const tabsContent = tv({
  base: `
    flex
    flex-1
    min-h-0
    overflow-auto
    outline-none
    flex-col
    data-[state=active]:animate-in
    data-[state=active]:fade-in-0
    data-[state=active]:slide-in-from-bottom-2
    data-[state=inactive]:animate-out
    data-[state=inactive]:fade-out-0
    data-[state=inactive]:slide-out-to-bottom-2
    data-[state=active]:duration-300
    data-[state=inactive]:duration-200
    data-[state=inactive]:absolute
    data-[state=inactive]:inset-0
  `
});
function TabsContent(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = tabsContent({
      className
    });
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className="relative has-data-[state=active]:flex-1 has-data-[state=active]:min-h-0 has-data-[state=active]:flex has-data-[state=active]:w-full"><TabsPrimitive.Content className={t1} data-slot="tabs-content" {...props} /></div>;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export { Tabs, tabsList, TabsList, tabsTrigger, TabsTrigger, TabsContent };